import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocateFixed, Maximize, Minus, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface CsCanvasProps {
    /**
     * Where the controls sit from the right edge.
     *
     * They were underneath the agent panel while it was open, so zooming and
     * recentring were simply unavailable whenever the vendor was talking to the
     * agent. The page passes the offset because only it knows how wide the
     * panel is and how far it floats.
     */
    rightOffset?: number;
    zoom: number;
    panX: number;
    panY: number;
    onZoomChange: (zoom: number) => void;
    onPanChange: (x: number, y: number) => void;
    onRecenter: (viewportWidth: number, viewportHeight: number) => void;
    children?: React.ReactNode;
    /** Rendered inside the transform (e.g. SVG connection layer) */
    backgroundLayer?: React.ReactNode;
    /** Rendered outside the transform (e.g. floating UI) */
    overlayLayer?: React.ReactNode;
    isEmpty?: boolean;
    emptyState?: React.ReactNode;
    /** Ref exposed so parent can call getBoundingClientRect for screen→canvas math */
    canvasRef?: React.Ref<HTMLDivElement>;
    /**
     * Fires when the user right-clicks on the empty canvas background.
     * Screen-space coordinates (viewport-relative) are provided so the
     * parent can position a floating menu. Suppressed when the click
     * originates inside a node — nodes can expose their own context
     * menus without fighting for the event.
     */
    onContextMenu?: (screenX: number, screenY: number) => void;
    /**
     * Fires on a left-click on the empty canvas background that did NOT
     * turn into a pan (mouse didn't move >3 px between down and up).
     * Used by the workspace to clear multi-selection when the user
     * clicks empty space.
     */
    onEmptyClick?: () => void;
    /**
     * Fires at the end of a Ctrl/Cmd/Shift+drag rubber-band gesture on
     * empty canvas. Receives a VIEWPORT-space rectangle (clientX/Y)
     * and an `additive` flag. The workspace is responsible for picking
     * which nodes intersect and updating selection.
     */
    onRubberBandSelect?: (
        box: { left: number; top: number; right: number; bottom: number },
        additive: boolean,
    ) => void;
}

const ZOOM_STEP = 0.15;
/** Exported so the workspace can clamp a restored viewport into range. */
export const ZOOM_MIN = 0.4;
/**
 * Never magnify past native size. Nodes are already large, and zooming in
 * further only ever showed one card at a time. The step is smaller to match:
 * a narrower range needs finer increments to stay usable.
 */
export const ZOOM_MAX = 1;

/**
 * Zoom a project opens at, and the level every reset returns to.
 *
 * Below 100% on purpose: nodes are large, and starting zoomed out shows the
 * shape of a graph rather than one card filling the viewport. Exported so the
 * workspace's several reset paths cannot drift apart from it.
 */
export const DEFAULT_ZOOM = 0.75;

/**
 * The Content Studio canvas primitive. Mirrors the IntegrationCanvas
 * pan/zoom/grid pattern so both features share one mental model.
 *
 * Key differences from IntegrationCanvas:
 *   - Ctrl+wheel zoom (more natural in 2026)
 *   - Pan only on empty background (children must stopPropagation on their
 *     own drag events)
 *   - Dotted grid background that scales with zoom so it feels "real"
 *   - Controls float in bottom-right (not top-left) — closer to how
 *     Figma, Krea, Miro place them
 */
export const CsCanvas: React.FC<CsCanvasProps> = ({
    rightOffset,
    zoom,
    panX,
    panY,
    onZoomChange,
    onPanChange,
    onRecenter,
    children,
    backgroundLayer,
    overlayLayer,
    isEmpty,
    emptyState,
    canvasRef,
    onContextMenu,
    onEmptyClick,
    onRubberBandSelect,
}) => {
    const { t } = useTranslation();
    const viewportRef = useRef<HTMLDivElement>(null);
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

    // Track whether the current pan gesture has actually moved — lets
    // us distinguish "panned the canvas" from "clicked empty space"
    // on mouseup so onEmptyClick can fire without eating pans.
    const panMovedRef = useRef(false);
    // Rubber-band selection. Toggle-only boolean for the effect gate;
    // the running coords live in a ref (to avoid re-registering the
    // listener every frame) AND in display state (to drive the
    // dashed-box overlay render).
    const [rubberBandActive, setRubberBandActive] = useState(false);
    const rubberBandRef = useRef<{
        startX: number;
        startY: number;
        currentX: number;
        currentY: number;
        additive: boolean;
    } | null>(null);
    const [rubberBandDisplay, setRubberBandDisplay] = useState<{
        left: number;
        top: number;
        width: number;
        height: number;
    } | null>(null);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            // Pan only on background (children stop propagation)
            if (e.button !== 0 && e.button !== 1) return;
            // Ctrl / Cmd / Shift on empty canvas starts a rubber-band
            // selection box instead of a pan. Shift extends selection;
            // Ctrl/Cmd replaces. Matches the convention used by most
            // design tools.
            if (e.ctrlKey || e.metaKey || e.shiftKey) {
                e.preventDefault();
                rubberBandRef.current = {
                    startX: e.clientX,
                    startY: e.clientY,
                    currentX: e.clientX,
                    currentY: e.clientY,
                    additive: e.shiftKey,
                };
                setRubberBandActive(true);
                return;
            }
            e.preventDefault();
            setIsPanning(true);
            panStartRef.current = { x: e.clientX, y: e.clientY, panX, panY };
            panMovedRef.current = false;
        },
        [panX, panY],
    );

    // Rubber-band mouse tracking. The effect only fires on active /
    // inactive toggle — not on every mouse move — so the document
    // listeners stay stable for the whole drag.
    useEffect(() => {
        if (!rubberBandActive) return;
        const handleMouseMove = (e: MouseEvent) => {
            const band = rubberBandRef.current;
            if (!band) return;
            band.currentX = e.clientX;
            band.currentY = e.clientY;
            setRubberBandDisplay({
                left: Math.min(band.startX, band.currentX),
                top: Math.min(band.startY, band.currentY),
                width: Math.abs(band.currentX - band.startX),
                height: Math.abs(band.currentY - band.startY),
            });
        };
        const handleMouseUp = () => {
            const band = rubberBandRef.current;
            if (band && onRubberBandSelect) {
                const left = Math.min(band.startX, band.currentX);
                const top = Math.min(band.startY, band.currentY);
                const right = Math.max(band.startX, band.currentX);
                const bottom = Math.max(band.startY, band.currentY);
                // Only fire if the user actually dragged a non-trivial
                // box — tiny jitter on a Ctrl+click is a no-op.
                if (right - left > 3 || bottom - top > 3) {
                    onRubberBandSelect({ left, top, right, bottom }, band.additive);
                }
            }
            rubberBandRef.current = null;
            setRubberBandDisplay(null);
            setRubberBandActive(false);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [rubberBandActive, onRubberBandSelect]);

    useEffect(() => {
        if (!isPanning) return;
        const handleMouseMove = (e: MouseEvent) => {
            const dx = e.clientX - panStartRef.current.x;
            const dy = e.clientY - panStartRef.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) panMovedRef.current = true;
            onPanChange(panStartRef.current.panX + dx, panStartRef.current.panY + dy);
        };
        const handleMouseUp = () => {
            if (!panMovedRef.current && onEmptyClick) onEmptyClick();
            setIsPanning(false);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isPanning, onPanChange, onEmptyClick]);

    // Wheel to zoom — two modes:
    //   1. Ctrl/Cmd + wheel → always zoom (standard canvas convention)
    //   2. Plain wheel on canvas background → zoom
    //   3. Plain wheel inside a node → let the node scroll
    //
    // We detect "inside a node" by checking for a [data-cs-node]
    // ancestor on the event target.
    // Zoom toward a screen point — adjusts pan so that point stays fixed
    const zoomToward = useCallback(
        (newZoom: number, screenX: number, screenY: number) => {
            const clampedZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoom));
            // Canvas point under the cursor before zoom
            const canvasX = (screenX - panX) / zoom;
            const canvasY = (screenY - panY) / zoom;
            // Adjust pan so the same canvas point stays under the cursor after zoom
            const newPanX = screenX - canvasX * clampedZoom;
            const newPanY = screenY - canvasY * clampedZoom;
            onPanChange(newPanX, newPanY);
            onZoomChange(clampedZoom);
        },
        [zoom, panX, panY, onZoomChange, onPanChange],
    );

    const handleWheel = useCallback(
        (e: React.WheelEvent) => {
            const insideNode = (e.target as HTMLElement).closest?.('[data-cs-node]');
            const hasModifier = e.ctrlKey || e.metaKey;

            if (insideNode && !hasModifier) return;

            e.preventDefault();
            const raw = -e.deltaY * 0.001;
            const clamped = Math.max(-0.15, Math.min(0.15, raw));
            const newZoom = zoom * (1 + clamped);
            // Zoom toward the cursor position
            const rect = viewportRef.current?.getBoundingClientRect();
            const screenX = e.clientX - (rect?.left || 0);
            const screenY = e.clientY - (rect?.top || 0);
            zoomToward(newZoom, screenX, screenY);
        },
        [zoom, zoomToward],
    );

    // Button zoom targets viewport center
    const handleZoomIn = () => {
        const rect = viewportRef.current?.getBoundingClientRect();
        const cx = (rect?.width || 800) / 2;
        const cy = (rect?.height || 600) / 2;
        zoomToward(zoom + ZOOM_STEP, cx, cy);
    };
    const handleZoomOut = () => {
        const rect = viewportRef.current?.getBoundingClientRect();
        const cx = (rect?.width || 800) / 2;
        const cy = (rect?.height || 600) / 2;
        zoomToward(zoom - ZOOM_STEP, cx, cy);
    };
    const handleRecenter = () => {
        if (!viewportRef.current) return;
        const rect = viewportRef.current.getBoundingClientRect();
        onRecenter(rect.width, rect.height);
    };
    const handleReset = () => {
        onPanChange(0, 0);
        onZoomChange(1);
    };

    const ctrlBtnClass = 'size-[34px] bg-background text-muted-foreground hover:text-foreground';

    // Merge external ref with internal ref
    const setRefs = useCallback((el: HTMLDivElement | null) => {
        (viewportRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (canvasRef) {
            if (typeof canvasRef === 'function') canvasRef(el);
            else (canvasRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }
    }, [canvasRef]);

    /**
     * Dot grid geometry.
     *
     * Spacing steps by powers of two so the dots keep roughly the same
     * apparent density at every zoom level — a fixed canvas-space spacing
     * crowds into mush when zoomed out and drifts too far apart when zoomed
     * in. The dot itself grows a little with zoom so it never thins to
     * nothing. Stepping means the grid visibly doubles at the thresholds,
     * which is the standard behaviour in infinite canvases.
     */
    const { gridSpacing, dotRadius } = useMemo(() => {
        const BASE = 20;          // canvas-space spacing at 100%
        const MIN_SCREEN = 15;    // below this, dots read as noise
        const MAX_SCREEN = 46;    // above this, the grid stops reading as one
        let spacing = BASE * zoom;
        while (spacing < MIN_SCREEN) spacing *= 2;
        while (spacing > MAX_SCREEN) spacing /= 2;
        return {
            gridSpacing: spacing,
            dotRadius: Math.min(2, Math.max(1.1, 1.15 * zoom)),
        };
    }, [zoom]);

    return (
        <div
            ref={setRefs}
            data-cs-canvas-viewport
            onMouseDown={handleMouseDown}
            onWheel={handleWheel}
            onContextMenu={(e: React.MouseEvent) => {
                // Only fire on empty background — nodes can define their
                // own right-click menus without fighting for the event.
                const insideNode = (e.target as HTMLElement).closest?.('[data-cs-node]');
                if (insideNode) return;
                if (!onContextMenu) return;
                e.preventDefault();
                onContextMenu(e.clientX, e.clientY);
            }}
            className={cn(
                'relative h-full w-full select-none overflow-hidden bg-muted',
                isPanning ? 'cursor-grabbing' : 'cursor-grab',
            )}
        >
            {/* Dot grid. Painted on the container rather than inside the
                transform, so pan moves it while zoom only changes its
                geometry — see the memo above. muted-foreground rather than the
                border token: at border strength the dots vanish into the
                canvas. */}
            <div
                className="pointer-events-none absolute inset-0 opacity-55"
                style={{
                    backgroundImage:
                        `radial-gradient(circle, var(--muted-foreground) ${dotRadius}px, transparent ${dotRadius}px)`,
                    backgroundSize: `${gridSpacing}px ${gridSpacing}px`,
                    backgroundPosition: `${panX}px ${panY}px`,
                }}
            />

            {/* The transform wrapper: everything inside is in canvas-space */}
            <div
                className="absolute inset-0 origin-top-left"
                style={{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})` }}
            >
                {backgroundLayer}
                {children}
            </div>

            {/* Overlay layer (not transformed) */}
            {overlayLayer}

            {/* Rubber-band selection rectangle — viewport-space, painted
                over everything. Faint ink tint + dashed border, following
                the Figma / Illustrator convention. */}
            {rubberBandDisplay && (() => {
                const viewportRect = viewportRef.current?.getBoundingClientRect();
                if (!viewportRect) return null;
                return (
                    <div
                        className="pointer-events-none absolute z-30 border-[1.5px] border-dashed border-foreground/60 bg-foreground/5"
                        style={{
                            left: rubberBandDisplay.left - viewportRect.left,
                            top: rubberBandDisplay.top - viewportRect.top,
                            width: rubberBandDisplay.width,
                            height: rubberBandDisplay.height,
                        }}
                    />
                );
            })()}

            {/* Empty state (centered) */}
            {isEmpty && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    {emptyState}
                </div>
            )}

            {/* Zoom / recenter controls — bottom-right, clear of the panel */}
            <div
                className="absolute bottom-5 z-20 flex flex-col gap-1.5 transition-all duration-200"
                style={{ right: rightOffset ?? 20 }}
            >
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleZoomIn}
                            className={ctrlBtnClass}
                            aria-label={t('CONTENT_STUDIO.ZOOM_IN', 'Zoom in') as string}
                        >
                            <Plus className="size-[18px]" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">{t('CONTENT_STUDIO.ZOOM_IN', 'Zoom in')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleZoomOut}
                            className={ctrlBtnClass}
                            aria-label={t('CONTENT_STUDIO.ZOOM_OUT', 'Zoom out') as string}
                        >
                            <Minus className="size-[18px]" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">{t('CONTENT_STUDIO.ZOOM_OUT', 'Zoom out')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleRecenter}
                            className={ctrlBtnClass}
                            aria-label={t('CONTENT_STUDIO.FIT_TO_SCREEN', 'Fit to screen') as string}
                        >
                            <Maximize className="size-[18px]" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">{t('CONTENT_STUDIO.FIT_TO_SCREEN', 'Fit to screen')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleReset}
                            className={ctrlBtnClass}
                            aria-label={t('CONTENT_STUDIO.RESET_VIEW', 'Reset view') as string}
                        >
                            <LocateFixed className="size-[18px]" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">{t('CONTENT_STUDIO.RESET_VIEW', 'Reset view')}</TooltipContent>
                </Tooltip>
                {/* Zoom % label */}
                <div className="mt-1 select-none text-center text-xs font-medium text-muted-foreground">
                    {Math.round(zoom * 100)}%
                </div>
            </div>
        </div>
    );
};
